import { useState, useRef, useEffect } from 'react';
import { X, QrCode, Search, Plus, Shirt } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import jsQR from 'jsqr';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GarmentAddStockModal({ onClose, onComplete }) {
  const { token } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setScanning(true);
        scanQRCode();
      }
    } catch (error) {
      toast.error('Failed to access camera');
    }
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const scan = () => {
      if (!streamRef.current) return;
      
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          handleQRScanned(code.data);
          return;
        }
      }
      
      animationRef.current = requestAnimationFrame(scan);
    };
    
    scan();
  };

  const handleQRScanned = async (qrCode) => {
    stopCamera();
    
    try {
      const response = await axios.get(`${API}/garment/qr-scan/${encodeURIComponent(qrCode)}`);
      
      if (!response.data.found) {
        toast.error(`No garment found for: ${qrCode}`);
        return;
      }
      
      const garmentItem = response.data.garment_item;
      setSelectedItem(garmentItem);
      setGarmentTypes(garmentItem.garment_types || []);
      
      // Auto-select last used type or first type
      if (garmentItem.garment_types?.length > 0) {
        setSelectedType(garmentItem.garment_types[0].garment_type);
      }
      
    } catch (error) {
      toast.error('Failed to find garment');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await axios.get(`${API}/garment/inventory?search=${encodeURIComponent(searchQuery)}&page_size=10`);
      setSearchResults(response.data.items || []);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleSelectFromSearch = async (item) => {
    try {
      const response = await axios.get(`${API}/garment/inventory/${item.id}/types`);
      setSelectedItem({ ...item, ...response.data.item });
      setGarmentTypes(response.data.garment_types || []);
      setSearchResults([]);
      setSearchQuery('');
      
      if (response.data.garment_types?.length > 0) {
        setSelectedType(response.data.garment_types[0].garment_type);
      }
    } catch (error) {
      toast.error('Failed to load item');
    }
  };

  const handleAddStock = async () => {
    if (!selectedItem || !selectedType || quantity <= 0) {
      toast.error('Please select item, type and enter quantity');
      return;
    }
    
    setProcessing(true);
    
    try {
      await axios.patch(
        `${API}/garment/inventory/${selectedItem.id}/type/${encodeURIComponent(selectedType)}/add_stock`,
        { quantity, remarks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added ${quantity} pieces to ${selectedType}`);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stock');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-emerald-50 to-green-50">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Add Garment Stock</h2>
            <p className="text-sm text-slate-500">
              {selectedItem ? selectedItem.fabric_name : 'Scan or search for garment'}
            </p>
          </div>
          <button onClick={onClose} className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
          {!selectedItem ? (
            <>
              {/* Search/Scan */}
              <div className="flex gap-2 mb-4">
                <Button
                  variant={scanning ? 'default' : 'outline'}
                  onClick={scanning ? stopCamera : startCamera}
                  className="gap-2"
                >
                  <QrCode className="w-4 h-4" />
                  {scanning ? 'Stop' : 'Scan QR'}
                </Button>
                <div className="flex-1 flex gap-2">
                  <Input
                    placeholder="Search fabric..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button variant="outline" onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {scanning && (
                <div className="relative bg-black rounded-lg overflow-hidden aspect-video mb-4">
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline />
                  <canvas ref={canvasRef} className="hidden" />
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="bg-white rounded-lg border">
                  {searchResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectFromSearch(item)}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                    >
                      {item.photo_url ? (
                        <img src={`${BACKEND_URL}${item.photo_url}`} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                          <Shirt className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{item.fabric_name}</p>
                        <p className="text-xs text-slate-500">{item.qr_code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Selected Item */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl mb-4">
                {selectedItem.photo_url ? (
                  <img src={`${BACKEND_URL}${selectedItem.photo_url}`} className="w-16 h-16 object-cover rounded-xl" />
                ) : (
                  <div className="w-16 h-16 bg-slate-200 rounded-xl flex items-center justify-center">
                    <Shirt className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-slate-800">{selectedItem.fabric_name}</p>
                  <p className="text-sm text-slate-500">{selectedItem.qr_code}</p>
                  <button
                    onClick={() => { setSelectedItem(null); setGarmentTypes([]); }}
                    className="text-xs text-blue-600 hover:underline mt-1"
                  >
                    Change Item
                  </button>
                </div>
              </div>

              {/* Garment Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Garment Type *
                </label>
                {garmentTypes.length > 0 ? (
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                  >
                    {garmentTypes.map((t) => (
                      <option key={t.garment_type} value={t.garment_type}>
                        {t.garment_type} (Available: {t.quantity_available})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter new type (e.g., Trouser)"
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Quantity *
                </label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                  min="1"
                />
              </div>

              {/* Remarks */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Remarks (optional)
                </label>
                <Input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional notes..."
                />
              </div>

              {/* Submit */}
              <Button
                onClick={handleAddStock}
                disabled={processing || !selectedType || quantity <= 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2"
              >
                <Plus className="w-5 h-5" />
                {processing ? 'Adding...' : `Add ${quantity} Pieces`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
